import {
  AddressType,
  applyUnsanitizedAddressLines,
  Coordinates,
  selectBillingAddressValues,
  selectShippingAddressValues,
  selectUnsanitizedBillingAddressLines,
  selectUnsanitizedShippingAddressLines,
} from '@buy-viasat/redux/src/address';
import {
  appActions,
  Country,
  CustomerType,
  Modals,
  QueryParams,
  selectCountry,
  selectCustomerType,
  selectFulfillmentAgreementId,
  selectIsBusinessUser,
  selectLocale,
  selectOrderId,
  selectPartyId,
  selectRelnId,
  selectSalesAgreementId,
  selectUrlParams,
} from '@buy-viasat/redux/src/app';
import { selectCartId, selectCartItems, selectOnetimeTotalWithPromo } from '@buy-viasat/redux/src/cart';
import { selectDateOfBirth, selectTaxId } from '@buy-viasat/redux/src/credit-verification';
import {
  navActions,
  Routes,
  RouteStates,
  selectCurrentAppRoute,
  selectRoutesStates,
} from '@buy-viasat/redux/src/navigator';
import {
  paymentInformationActions,
  PaymentType,
  selectNonRegulatedSpbBillingAccountId,
  selectPaymentOnFileId,
  selectRegulatedSpbBillingAccountId,
  selectSmbBillingAccountId,
  selectSpbBillingAccountId,
  selectTaxCode,
  selectTransactionId,
  selectVppPPaymentType,
} from '@buy-viasat/redux/src/payment-information';
import {
  selectEmail,
  selectFirstName,
  selectLastName,
  selectPhone,
  validatePersonalInformation,
} from '@buy-viasat/redux/src/personal-information';
import {
  scheduleInstallationActions,
  SelectedInstallation,
  selectPALInstallationDates,
  selectSelectedInstallation,
} from '@buy-viasat/redux/src/schedule-installation';
import {
  selectCoordinates,
  selectIsVermontCustomer,
  selectServiceAddressValues,
  selectUnsanitizedAddressLines,
} from '@buy-viasat/redux/src/serviceability';
import { sanitizeAddressLines } from '@buy-viasat/redux/src/plan';
import {
  CartItems,
  ErrorTypes,
  GetAddOnsPayload,
  LocationInput,
  PartyType,
  ScheduleR0,
  SubmitFullOrderInput,
  TaxCodeInput,
  WorkOrderInformation,
} from '@buy-viasat/types/build/bv';
import {
  selectAccountHolderEmail,
  selectAccountHolderFirstName,
  selectAccountHolderLastName,
  selectOnSiteContactEmail,
  selectOnSiteContactFirstName,
  selectOnSiteContactLastName,
  selectOnSiteContactPhone,
} from '@buy-viasat/redux/src/business-information';
import { ActionCreatorWithoutPayload, PayloadAction } from '@reduxjs/toolkit';

import { call, CallEffect, delay, fork, put, PutEffect, select, SelectEffect, takeLatest } from 'redux-saga/effects';
import { RouteActions } from 'shared/Navigator/types';
import { adobeTargetDataLayer } from 'shared/containers/Analytics';

import { benchmark } from 'shared/containers/App/saga';

import { v4 as uuid } from 'uuid';
import submitFullOrderAsync from '../providers/requests/order/submitFullOrder';
import { getBillingAccountIdToDeactivate } from '../utils';
import clientLogger from '../utils/clientLogger';
import { navigate } from '../utils/navigationService';
import { DateOfBirth, FormFieldState } from '../views/Checkout/types';
import { selectCreditVerificationValidation } from '../views/CreditVerification/selectors';
import { creditVerificationActions } from '../views/CreditVerification/slice';
import { personalInformationActions } from '../views/PersonalInformation/slice';
import { constructWorkOrderInformation } from './utils';

import { USE_NEW_API } from 'shared/utils/dataFetchingHooks/constants';
import { InvalidTransactionErrorMessage } from 'shared/types';
import { selectAvailableAddons } from '@buy-viasat/redux/src/addons';

export function* submitFullOrderError(err: any): Generator {
  clientLogger('submitFullOrderError: ', err);
  yield put(appActions.setIsOrderSubmitting(false));
  yield put(appActions.setIsCheckoutButtonDisabled(false));
  yield put(paymentInformationActions.resetTransactionId());
  yield put(paymentInformationActions.setVPPPostMessageFailure());

  if (err.message === ErrorTypes.PaymentAuthError) {
    yield put(appActions.setModalVisible(Modals.AUTH_ERROR));
  } else {
    if (err.message.includes(InvalidTransactionErrorMessage)) {
      yield put(appActions.setErrorRetryAction({ type: navActions.onEditPaymentInformationPress.type, payload: null }));
    } else {
      yield put(appActions.setErrorRetryAction({ type: appActions.onModalCancel.type, payload: null }));
    }
    yield put(appActions.setModalVisible(Modals.ERROR));
  }
}

function* navigateToRoute(route: Routes): Generator<SelectEffect | PutEffect | CallEffect, void, string & PartyType> {
  const locale: string = yield select(selectLocale);
  const customerType: string = yield select(selectCustomerType);
  const urlPrefix = `/${locale}/${customerType}`;

  yield put(navActions.setMaxRoute(route));
  yield put(navActions.setCurrentAppRoute(route));
  // navigate() calls the fn stored in navigationService (replaces v5 history.push).
  yield call(navigate, `${urlPrefix}/${route}`);
}

// Handles pushes to history for routing.  Only used for direct routes such as edit buttons
function* onRouteUserToSaga(action: PayloadAction<Routes>) {
  yield call(navigateToRoute, action.payload);
}

// Handles pushes to history for routing on Next, Previous or Reset to default route
export function onRouteSaga(routeAction: RouteActions) {
  return function* navigateToNewRoute(): Generator<
    SelectEffect | PutEffect | CallEffect,
    void,
    string & PartyType & Routes
  > {
    const currentRoute: Routes = yield select(selectCurrentAppRoute);
    const routeState: RouteStates = yield select(selectRoutesStates);

    const newRoute = routeState[currentRoute as Routes][routeAction];

    if (newRoute === undefined) {
      return;
    }

    yield call(navigateToRoute, newRoute);
  };
}

function* setupAppRoutes() {
  const isVermontCustomer: boolean = yield select(selectIsVermontCustomer);
  const isBusinessUser: boolean = yield select(selectIsBusinessUser);

  const cartItems: CartItems = yield select(selectCartItems);
  const explicitConsent = cartItems?.plan?.explicitConsent ?? false;

  const addonOffers: GetAddOnsPayload = yield select(selectAvailableAddons);
  const shouldSkipAddons = !addonOffers?.addOns.length && !addonOffers?.addOnGroup?.addOns?.length;

  yield put(
    navActions.computeRoutes({
      skipCreditCheck: isVermontCustomer || isBusinessUser,
      skipAddons: shouldSkipAddons,
      skipDisclosures: !explicitConsent,
    }),
  );
}

function* onCreditCheckButtonPress() {
  yield put(creditVerificationActions.validateCreditVerification({ isTaxId: false }));
  yield put(creditVerificationActions.validateDateOfBirth());

  const formValid: boolean = yield select(selectCreditVerificationValidation);

  if (formValid) {
    yield put(creditVerificationActions.checkCustomerCredit());
    //navigate forwards
    yield put(navActions.next());
  }

  return;
}

export function* onPaymentInformationButtonPress() {
  const relnId: string = yield select(selectRelnId);
  const cartId: string = yield select(selectCartId);
  const serviceAddress: AddressType = yield select(selectServiceAddressValues);
  const unsanitizedAddressLines: string[] | null = yield select(selectUnsanitizedAddressLines);
  const billingAddress: AddressType = yield select(selectBillingAddressValues);
  const shippingAddress: AddressType = yield select(selectShippingAddressValues);
  const unsanitizedBillingAddressLines: string[] | null = yield select(selectUnsanitizedBillingAddressLines);
  const unsanitizedShippingAddressLines: string[] | null = yield select(selectUnsanitizedShippingAddressLines);
  const coordinates: Coordinates = yield select(selectCoordinates);
  const spbBillingAccountId: string = yield select(selectSpbBillingAccountId);
  const transactionId: string = yield select(selectTransactionId);
  const installationDate: FormFieldState<SelectedInstallation> = yield select(selectSelectedInstallation);
  const PALInstallationDates: ScheduleR0[] = yield select(selectPALInstallationDates);
  const onSiteContactFirstName: string = yield select(selectOnSiteContactFirstName);
  const onSiteContactLastName: string = yield select(selectOnSiteContactLastName);
  const onSiteContactEmail: string = yield select(selectOnSiteContactEmail);
  const setOnSiteContactPhoneNumber: string = yield select(selectOnSiteContactPhone);
  const workOrderInformation: WorkOrderInformation | null = yield call(
    constructWorkOrderInformation,
    installationDate.value,
    PALInstallationDates,
    {
      firstName: onSiteContactFirstName,
      lastName: onSiteContactLastName,
      email: onSiteContactEmail,
      phoneNumber: setOnSiteContactPhoneNumber,
    },
  );
  const taxId: FormFieldState<string> = yield select(selectTaxId);
  const paymentOnFileId: string = yield select(selectPaymentOnFileId);
  const urlParams: QueryParams = yield select(selectUrlParams);
  const paymentType: PaymentType = yield select(selectVppPPaymentType);
  const partyId: string = yield select(selectPartyId);
  const dob: DateOfBirth<string, string, string> = yield select(selectDateOfBirth);
  const salesAgreementId: string = yield select(selectSalesAgreementId);
  const fulfillmentAgreementId: string = yield select(selectFulfillmentAgreementId);
  const regulatedSPBBillingAccountId: string = yield select(selectRegulatedSpbBillingAccountId);
  const nonRegulatedSPBBillingAccountId: string = yield select(selectNonRegulatedSpbBillingAccountId);
  const smbBillingAccountId: string = yield select(selectSmbBillingAccountId);
  const phoneNumber: FormFieldState<string> = yield select(selectPhone);
  const customerType: CustomerType = yield select(selectCustomerType);
  const country: Country = yield select(selectCountry);
  const totalWithPromos: number = yield select(selectOnetimeTotalWithPromo);
  const taxJurisdiction: TaxCodeInput = yield select(selectTaxCode);
  const { label, taxCodeType, taxCodeValue } = taxJurisdiction;

  let email = '';
  let firstName = '';
  let lastName = '';

  switch (customerType) {
    case CustomerType.B:
      firstName = yield select(selectAccountHolderFirstName);
      lastName = yield select(selectAccountHolderLastName);
      email = yield select(selectAccountHolderEmail);
      break;
    case CustomerType.R:
      const firstNameField: FormFieldState<string> = yield select(selectFirstName);
      firstName = firstNameField.value;
      const lastNameField: FormFieldState<string> = yield select(selectLastName);
      lastName = lastNameField.value;
      const emailField: FormFieldState<string> = yield select(selectEmail);
      email = emailField.value;

      break;
  }

  let orderId: string = yield select(selectOrderId);
  if (!orderId.length) {
    orderId = uuid();
    yield put(appActions.setOrderId(orderId));
  }

  yield put(appActions.setIsOrderSubmitting(true));
  const deactivateBillingAccountArg = {
    nonRegulatedSPBBillingAccountId,
    smbBillingAccountId,
    regulatedSPBBillingAccountId,
    spbBillingAccountId,
  };
  const deactivateAccountIdList = getBillingAccountIdToDeactivate(deactivateBillingAccountArg);

  const baseInput: SubmitFullOrderInput = {
    customerRelationshipId: relnId,
    cartId,
    billingAccountId: spbBillingAccountId,
    billingAccountIdToDeactivateList: deactivateAccountIdList,
    orderId,
    workOrderInformation,
    partyId,
    payment: {
      transactionId,
      userAgent: navigator.userAgent,
      paymentOnFileId,
      paymentType,
    },
    customer: {
      taxId: taxId.value,
      // Match getAvailableProducts/getAddons: send the exact addressLines scrubAddress returned.
      // Billing/shipping fall back to the service unsanitized lines when they mirror the service
      // address (no separate scrub), so all three carry the same unsanitized value when possible.
      serviceLocation: sanitizeAddressLines(
        { address: serviceAddress, coordinates } as LocationInput,
        unsanitizedAddressLines,
      ),
      billingAddress: applyUnsanitizedAddressLines(
        billingAddress,
        unsanitizedBillingAddressLines ?? unsanitizedAddressLines,
      ),
      shippingAddress: applyUnsanitizedAddressLines(
        shippingAddress,
        unsanitizedShippingAddressLines ?? unsanitizedAddressLines,
      ),
      firstName,
      lastName,
      phoneNumber: phoneNumber.value,
      email,
    },
    attributes: {
      affiliateId: urlParams.affiliateId,
      sessionId: urlParams.sessionId,
      campaignId: urlParams.campaignId,
      adobeEcId: uuid(adobeTargetDataLayer?.getState?.()?.userData?.ecid),
      dealerId: urlParams.dealerId,
      partnerId: urlParams.partnerId,
    },
    salesAgreementId,
    fulfillmentAgreementId,
    customerType,
    country,
    taxJurisdiction: { label, taxCodeType, taxCodeValue },
    upfrontFee: totalWithPromos,
  };

  const month = dob.month.value;
  const day = dob.day.value;
  const year = dob.year.value;

  const input = USE_NEW_API ? { ...baseInput, dob: { month, day, year } } : baseInput;

  try {
    yield put(appActions.setIsOrderSubmitting(true));

    const { data = {} } = yield call(submitFullOrderAsync, input);

    yield put(appActions.setExternalWorkOrderId(data.submitFullOrder.externalWorkOrderId));
    yield put(navActions.routeUserTo(Routes.ORDER_SUMMARY));
  } catch (err: any) {
    yield call(submitFullOrderError, err);
  }
}

export function* onPersonalInformationButtonPress() {
  const isVermontCustomer: boolean = yield select(selectIsVermontCustomer);
  const personalInfoValid: boolean = yield select(validatePersonalInformation);

  if (!personalInfoValid) {
    if (isVermontCustomer) {
      yield put(creditVerificationActions.validateDateOfBirth());
    }
    //below yield sets up sequence that leads to page navigation
    yield put(personalInformationActions.validateBillingAddress());
  }
}

export function* checkScheduledInstallation() {
  const selectedInstallation: FormFieldState<SelectedInstallation> = yield select(selectSelectedInstallation);
  const {
    value: { month, day, year, arrivalWindow },
  } = selectedInstallation;

  const dateIsSelected = Boolean(month && day && year);
  const timeIsSelected = arrivalWindow !== '';

  if (dateIsSelected === timeIsSelected) {
    yield put(appActions.setIsScheduleInstallationComplete(true));
    yield put(navActions.next());
  } else {
    yield put(scheduleInstallationActions.setHasSubmittedScheduleInstall(true));
  }
}

function* handleCheckoutButtonPress(action: PayloadAction<Routes>) {
  switch (action.payload) {
    case Routes.PERSONAL_INFORMATION:
      yield call(onPersonalInformationButtonPress);
      break;
    case Routes.CREDIT_CHECK:
      yield call(onCreditCheckButtonPress);
      break;
    case Routes.SCHEDULE_INSTALLATION:
      yield call(checkScheduledInstallation);
      break;
  }
}

function* debounceOnRouteButtonPress(action: PayloadAction<Routes>) {
  yield delay(300);
  yield fork(handleCheckoutButtonPress, action);
}

// handle user edits of their information initiated from the CustomerLineItems
export function* handleEditButtonPress(action: ActionCreatorWithoutPayload) {
  const billingAccountId: string = yield select(selectSpbBillingAccountId);
  switch (action.type) {
    case navActions.onEditPaymentInformationPress.type:
      yield put(navActions.routeUserTo(Routes.PAYMENT_INFORMATION));
      yield put(paymentInformationActions.createVPPTransaction(billingAccountId));
      break;
    case navActions.onEditPersonalInformationPress.type:
      yield put(navActions.routeUserTo(Routes.PERSONAL_INFORMATION));
      break;
    case navActions.onEditScheduleInstallationPress.type:
      yield put(navActions.routeUserTo(Routes.SCHEDULE_INSTALLATION));
      break;
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export function* routerSaga(): Generator {
  // Direct routes
  yield takeLatest(navActions.routeUserTo.type, onRouteUserToSaga);

  // Checkout button presses
  yield takeLatest(navActions.onRouteButtonPress.type, benchmark<Routes>(debounceOnRouteButtonPress));

  // User information edit link handlers in the cart
  yield takeLatest(navActions.onEditPaymentInformationPress.type, handleEditButtonPress);
  yield takeLatest(navActions.onEditPersonalInformationPress.type, handleEditButtonPress);
  yield takeLatest(navActions.onEditScheduleInstallationPress.type, handleEditButtonPress);
  yield takeLatest(navActions.next.type, onRouteSaga(RouteActions.Next));
  yield takeLatest(navActions.previous.type, onRouteSaga(RouteActions.Previous));
  yield takeLatest(navActions.reset.type, onRouteSaga(RouteActions.Reset));
  yield takeLatest(navActions.setupAppRoutes.type, setupAppRoutes);
}
